import { useEffect, useRef } from 'react';

// Keyboard focus after the canvas changes under it (build plan §5.7 a11y).
// Deleting a field removes the button that had focus (its card, its ⋯
// sheet, the Properties Delete), so the browser drops focus on <body>.
// This puts it on the card that took the deleted one's place, or the one
// before it, or the canvas itself when none are left.

export const CANVAS_ID = 'fb-canvas';

const focusEl = (el) => {
  if (!el || typeof el.focus !== 'function') return false;
  el.focus();
  return document.activeElement === el;
};

// The card's select button, or the canvas when the card is gone.
export const focusField = (id) => {
  const card = id ? document.getElementById(`fb-field-${id}`) : null;
  const button = card && card.querySelector('[data-fb-select]');
  return focusEl(button) || focusEl(document.getElementById(CANVAS_ID));
};

export const focusLost = () => {
  const active = document.activeElement;
  return !active || active === document.body || !active.isConnected;
};

// Exactly one field gone → the id now at its place (or the new last one),
// or null when nothing was removed / nothing is left.
export const neighbourId = (before, after) => {
  if (!before || !after || before.length - after.length !== 1) return null;
  const kept = new Set(after.map((f) => f.id));
  const at = before.findIndex((f) => !kept.has(f.id));
  if (at < 0 || !after.length) return null;
  return after[Math.min(at, after.length - 1)].id;
};

const useFieldFocus = (fields) => {
  const previous = useRef(fields);
  useEffect(() => {
    const before = previous.current;
    previous.current = fields;
    if (before === fields || before.length - fields.length !== 1) return;
    if (focusLost()) focusField(neighbourId(before, fields));
  }, [fields]);
};

export default useFieldFocus;
